import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ConnectionStatus, Network } from '@capacitor/network';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ToastSandbox } from '@app/core/sandboxes/toast.sandbox';
import { TranslationSandbox } from '@app/core/sandboxes/translation.sandbox';

@Injectable({
  providedIn: 'root',
})
export class ConnectivityService {
  private readonly toastSandbox = inject(ToastSandbox);
  private readonly translationSandbox = inject(TranslationSandbox);

  readonly isOffline = signal(false);

  private readonly isOffline$ = toObservable(this.isOffline);

  private blockedRequestToastLocked = false;

  async init(): Promise<void> {
    const connected = await this.getConnectivity();
    if (!connected) {
      this.reportConnectionLost();
    }

    await Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
      if (status.connected) {
        this.reportConnectionRestored();
      } else {
        this.reportConnectionLost();
      }
    });
  }

  async getConnectivity(): Promise<boolean> {
    const status = await Network.getStatus();
    return status.connected;
  }

  whenOnline(): Observable<void> {
    return this.isOffline$.pipe(
      filter((offline) => !offline),
      take(1),
      map(() => undefined)
    );
  }

  reportConnectionLost(): void {
    if (this.isOffline()) {
      return;
    }

    this.isOffline.set(true);
    this.showOfflineToast();
  }

  reportConnectionRestored(): void {
    if (!this.isOffline()) {
      return;
    }

    this.isOffline.set(false);
    this.showOnlineToast();
  }

  notifyBlockedRequest(): void {
    if (this.blockedRequestToastLocked) {
      return;
    }

    this.blockedRequestToastLocked = true;
    this.showOfflineToast();
    setTimeout(() => {
      this.blockedRequestToastLocked = false;
    }, this.toastSandbox.defaultConfig.duration as number);
  }

  private showOnlineToast(): void {
    this.toastSandbox.showToast({
      icon: 'cloud-done-outline',
      message: this.translateWithFallback('connectivity.online', 'You’re back online.'),
    });
  }

  private showOfflineToast(): void {
    this.toastSandbox.showToast({
      icon: 'cloud-offline-outline',
      message: this.translateWithFallback('connectivity.offline', 'It looks like you’re offline. Please retry your connection.'),
    });
  }

  private translateWithFallback(key: string, fallback: string): string {
    const translated = this.translationSandbox.translate(key);
    return translated.includes(key) ? fallback : translated;
  }
}
